import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { DBNotification } from "../types";
import { HISTORY_KEY, INBOX_KEY } from "../queryKeys";
import { extractEventId } from "../lib/format";
import {
  breakfastEventsPrefix,
  pendingInvitesPrefix,
} from "@/features/breakfast/queryKeys";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";

/**
 * Fallback breakfast-RSVP action for `BREAKFAST_INVITE` / `EVENT_UPDATE`
 * notifications that reached `NotifRow` because no first-class
 * `BreakfastInviteCard` covered them (e.g. the pending `breakfast_attendees`
 * row was already consumed or the sidebar feed is stale).
 *
 * Resolves the target event id by:
 *
 *   1. Parsing `notif.action_url` via `extractEventId` (handles the
 *      three historical shapes — raw UUID, `/events/:id` path, null).
 *   2. Falling back to the caller's most-recent pending
 *      `breakfast_attendees` row — covers pre-Phase 16 notifications
 *      where `action_url` was null.
 *
 * Dispatches through the `handle_breakfast_rsvp` Postgres RPC which
 * handles status + notification side effects server-side, then archives
 * the triggering notification so it doesn't re-surface.
 */
export function useBreakfastNotifActions() {
  const queryClient = useQueryClient();

  const breakfastRsvp = useMutation({
    mutationFn: async ({
      notif,
      accept,
    }: {
      notif:  DBNotification;
      accept: boolean;
    }) => {
      // action_url has historically carried three different shapes:
      //
      //   • Phase 16 RPC        — raw UUID        ("7f2b3a…")
      //   • Phase 25 trigger    — route path      ("/events/7f2b3a…")
      //   • pre-Phase 16        — `null`
      //
      // `extractEventId` normalises the first two; the last falls through
      // to the breakfast_attendees lookup so the user can still act on
      // legacy notifications (they're rare but exist in prod backfills).
      let eventId: string | null = extractEventId(notif.action_url);

      if (!eventId) {
        const { data: session } = await supabase.auth.getSession();
        const meId = session.session?.user?.id;
        if (!meId) throw new Error("Not authenticated.");

        const { data: rows, error: lookupErr } = await supabase
          .from("breakfast_attendees")
          .select("event_id")
          .eq("user_id", meId)
          .eq("status", "pending")
          .order("created_at", { ascending: false })
          .limit(1);

        if (lookupErr) throw lookupErr;
        eventId = rows?.[0]?.event_id ?? null;
      }

      if (!eventId) {
        throw new Error(
          "Could not locate the event. Please refresh and try again.",
        );
      }

      const { error } = await supabase.rpc("handle_breakfast_rsvp", {
        p_event_id:   eventId,
        p_status:     accept ? "confirmed" : "declined",
        p_bring_item: null,
      });
      if (error) throw error;

      // Archive the notification after successful RSVP
      await supabase
        .from("notifications")
        .update({ is_read: true, is_archived: true })
        .eq("id", notif.id);

      return { accept };
    },
    onMutate: async ({ notif }) => {
      await queryClient.cancelQueries({ queryKey: INBOX_KEY });
      const prev = queryClient.getQueryData<DBNotification[]>(INBOX_KEY);
      queryClient.setQueryData<DBNotification[]>(INBOX_KEY, (old = []) =>
        old.filter((n) => n.id !== notif.id),
      );
      return { prev };
    },
    onSuccess: () => {
      // Sprint 1.5/2 polish: success is silent. The invite row animates
      // out of the bell and (on accept) the event appears on the
      // calendar — both are stronger confirmations than a toast.
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      queryClient.invalidateQueries({
        queryKey:    breakfastEventsPrefix,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey:    pendingInvitesPrefix,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey:    trackedDaysPrefix,
        refetchType: "all",
      });
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(INBOX_KEY, ctx.prev);
      toast({
        title:       "Could not update RSVP",
        description: err.message,
        variant:     "destructive",
      });
    },
  });

  return { breakfastRsvp };
}
